import { useMemo } from "react";
import "./MiniRadar.css";

const SIZE = 220;
const CENTER = SIZE / 2;
const PADDING = 14;
const RADIUS = CENTER - PADDING;
const KM_PER_DEG = 111;
const EMERGENCY_SQUAWKS = new Set(["7500", "7600", "7700"]);
const RINGS = [0.25, 0.5, 0.75, 1];

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function computeCentroid(list) {
  let latSum = 0;
  let lonSum = 0;
  let count = 0;
  for (const a of list) {
    const lat = toNumber(a.latitude);
    const lon = toNumber(a.longitude);
    if (lat == null || lon == null) continue;
    latSum += lat;
    lonSum += lon;
    count += 1;
  }
  if (count === 0) return null;
  return { lat: latSum / count, lon: lonSum / count };
}

export default function MiniRadar({
  aircraft,
  center,
  selectedId,
  onSelect,
  rangeKm = 150,
}) {
  const list = Array.isArray(aircraft) ? aircraft : [];

  const origin = useMemo(() => {
    const lat = toNumber(center?.lat);
    const lon = toNumber(center?.lon);
    if (lat != null && lon != null) return { lat, lon };
    return computeCentroid(list);
  }, [center?.lat, center?.lon, list]);

  const blips = useMemo(() => {
    if (!origin) return [];
    const clatRad = (origin.lat * Math.PI) / 180;
    const cosLat = Math.cos(clatRad);
    const out = [];

    for (const a of list) {
      const lat = toNumber(a.latitude);
      const lon = toNumber(a.longitude);
      if (lat == null || lon == null) continue;

      // Equirectangular approximation.
      const dx = (lon - origin.lon) * cosLat * KM_PER_DEG; // east km
      const dy = (lat - origin.lat) * KM_PER_DEG; // north km
      const distKm = Math.hypot(dx, dy);
      if (distKm > rangeKm) continue; // clip beyond range

      const scale = RADIUS / rangeKm;
      // Screen coords: x east, y down (invert north).
      const x = CENTER + dx * scale;
      const y = CENTER - dy * scale;

      const squawk = a.squawk != null ? String(a.squawk) : null;
      const emergency = squawk != null && EMERGENCY_SQUAWKS.has(squawk);
      const heading = toNumber(a.heading);

      out.push({
        icao24: a.icao24,
        x,
        y,
        heading,
        emergency,
        squawk,
        distKm,
        selected: a.icao24 != null && a.icao24 === selectedId,
      });
    }
    return out;
  }, [list, origin, rangeKm, selectedId]);

  const ticks = [
    { label: "N", x: CENTER, y: PADDING - 2, anchor: "middle", dy: "0.35em" },
    { label: "E", x: SIZE - PADDING + 2, y: CENTER, anchor: "start", dy: "0.35em" },
    { label: "S", x: CENTER, y: SIZE - PADDING + 2, anchor: "middle", dy: "0.35em" },
    { label: "W", x: PADDING - 2, y: CENTER, anchor: "end", dy: "0.35em" },
  ];

  const ariaLabel = origin
    ? `Mini radar scope, range ${rangeKm} kilometres, showing ${blips.length} of ${list.length} aircraft`
    : "Mini radar scope, no aircraft position data available";

  return (
    <section className="mini-radar" aria-label="Mini radar">
      <svg
        className="mini-radar-scope"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width={SIZE}
        height={SIZE}
        role="img"
        aria-label={ariaLabel}
      >
        <defs>
          <clipPath id="mini-radar-clip">
            <circle cx={CENTER} cy={CENTER} r={RADIUS} />
          </clipPath>
        </defs>

        <circle
          className="mini-radar-face"
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
        />

        <g className="mini-radar-rings">
          {RINGS.map((r) => (
            <circle
              key={r}
              cx={CENTER}
              cy={CENTER}
              r={RADIUS * r}
              className="mini-radar-ring"
            />
          ))}
          <line
            x1={CENTER}
            y1={PADDING}
            x2={CENTER}
            y2={SIZE - PADDING}
            className="mini-radar-ring"
          />
          <line
            x1={PADDING}
            y1={CENTER}
            x2={SIZE - PADDING}
            y2={CENTER}
            className="mini-radar-ring"
          />
        </g>

        <g className="mini-radar-sweep" clipPath="url(#mini-radar-clip)">
          <line
            x1={CENTER}
            y1={CENTER}
            x2={CENTER}
            y2={PADDING}
            className="mini-radar-sweep-line"
          />
        </g>

        <g className="mini-radar-ticks" aria-hidden="true">
          {ticks.map((t) => (
            <text
              key={t.label}
              x={t.x}
              y={t.y}
              textAnchor={t.anchor}
              dy={t.dy}
              className="mini-radar-tick"
            >
              {t.label}
            </text>
          ))}
        </g>

        <g clipPath="url(#mini-radar-clip)">
          {blips.map((b) => {
            const className = [
              "mini-radar-blip",
              b.selected ? "is-selected" : "",
              b.emergency ? "is-emergency" : "",
            ]
              .filter(Boolean)
              .join(" ");
            const title = `${b.icao24 ?? "unknown"} · ${Math.round(
              b.distKm
            )} km${b.squawk ? ` · squawk ${b.squawk}` : ""}`;
            const handleActivate = () => {
              if (typeof onSelect === "function" && b.icao24 != null) {
                onSelect(b.icao24);
              }
            };
            return (
              <g
                key={b.icao24 ?? `${b.x},${b.y}`}
                className={className}
                transform={`translate(${b.x} ${b.y})`}
                role="button"
                tabIndex={0}
                aria-label={title}
                onClick={handleActivate}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleActivate();
                  }
                }}
              >
                <title>{title}</title>
                {b.heading != null ? (
                  <path
                    className="mini-radar-chevron"
                    d="M0 -5 L4 4 L0 1.5 L-4 4 Z"
                    transform={`rotate(${b.heading})`}
                  />
                ) : (
                  <circle className="mini-radar-dot" r={3} />
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </section>
  );
}
